import { Box, Card, Grid, Typography, useTheme } from '@mui/material'
import { PhotoConsumer } from 'react-photo-view'
import ReactPlayer from 'react-player'
import { saveAs } from 'file-saver'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import DescriptionIcon from '@mui/icons-material/Description'
import TableChartIcon from '@mui/icons-material/TableChart'
import TextSnippetIcon from '@mui/icons-material/TextSnippet'
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile'

const AttachedFileCard = ({ attachments, baseUrl, token }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const getFileExtension = (path) => {
    if (!path) return null
    const parts = path.split('.')
    return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : null
  }

  const getFileIcon = (extension) => {
    switch (extension) {
      case 'pdf':
        return <PictureAsPdfIcon sx={{ fontSize: 32, color: '#e53935' }} />
      case 'doc':
      case 'docx':
        return <DescriptionIcon sx={{ fontSize: 32, color: '#1976d2' }} />
      case 'xls':
      case 'xlsx':
        return <TableChartIcon sx={{ fontSize: 32, color: '#43a047' }} />
      case 'txt':
        return <TextSnippetIcon sx={{ fontSize: 32, color: '#757575' }} />
      default:
        return <InsertDriveFileIcon sx={{ fontSize: 32, color: '#9e9e9e' }} />
    }
  }

  const renderImageFile = (img) => {
    const url = `${baseUrl}${img.path_it}&token=${token}`
    return (
      <Grid item xs={6} sm={4} md={3} lg={2.4} key={`IMG${img.path_it}`}>
        <PhotoConsumer src={url} intro={img.path_it}>
          <Card
            sx={{
              background: isDarkMode ? 'linear-gradient(135deg, #2d3a52 0%, #1F2A40 100%)' : 'linear-gradient(135deg, #fff 0%, #f8f9fa 100%)',
              p: 0.75,
              borderRadius: 1.5,
              boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
              border: isDarkMode ? '1px solid #3d4a62' : '1px solid #e0e0e0',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              '&:hover': {
                transform: 'translateY(-1px)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                borderColor: isDarkMode ? '#90caf9' : '#1976d2'
              }
            }}
          >
            <Box
              sx={{
                width: '100%',
                height: 70,
                borderRadius: 1,
                overflow: 'hidden',
                background: isDarkMode ? '#1F2A40' : '#f5f5f5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 0.5
              }}
            >
              <img
                src={url}
                alt={img?.nombre_it}
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain'
                }}
              />
            </Box>
            <Typography
              sx={{
                color: isDarkMode ? '#b0bec5' : '#424242',
                fontSize: 9,
                fontWeight: 500,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                textAlign: 'center'
              }}
            >
              {img?.nombre_it}
            </Typography>
          </Card>
        </PhotoConsumer>
      </Grid>
    )
  }

  const renderVideoFile = (img) => {
    const url = `${baseUrl}${img.path_it}&token=${token}`
    return (
      <Grid item xs={12} sm={6} md={4} key={`Video${img.path_it}`}>
        <Card
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            p: 0.75,
            borderRadius: 1.5,
            boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
            transition: 'all 0.2s ease',
            '&:hover': {
              transform: 'translateY(-1px)',
              boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
            }
          }}
        >
          <Box
            sx={{
              borderRadius: 1,
              overflow: 'hidden',
              background: '#000',
              mb: 0.5
            }}
          >
            <ReactPlayer url={url} width="100%" height="100px" controls />
          </Box>
          <Typography
            sx={{
              color: '#fff',
              fontSize: 9,
              fontWeight: 500,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              textAlign: 'center'
            }}
          >
            {img?.nombre_it}
          </Typography>
        </Card>
      </Grid>
    )
  }

  const renderDocumentFile = (img, extension) => {
    const url = `${baseUrl}${img.path_it}&token=${token}`
    
    const gradientColors = isDarkMode ? {
      pdf: { bg: 'linear-gradient(135deg, #4a1515 0%, #3d1a1a 100%)', border: '#6d2828' },
      doc: { bg: 'linear-gradient(135deg, #1a3a5c 0%, #1F2A40 100%)', border: '#3d4a62' },
      docx: { bg: 'linear-gradient(135deg, #1a3a5c 0%, #1F2A40 100%)', border: '#3d4a62' },
      xls: { bg: 'linear-gradient(135deg, #1a3d2e 0%, #1F2A40 100%)', border: '#2d4a3a' },
      xlsx: { bg: 'linear-gradient(135deg, #1a3d2e 0%, #1F2A40 100%)', border: '#2d4a3a' },
      txt: { bg: 'linear-gradient(135deg, #3d2e1a 0%, #2d3a52 100%)', border: '#4a3a2a' }
    } : {
      pdf: { bg: 'linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)', border: '#ef9a9a' },
      doc: { bg: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)', border: '#90caf9' },
      docx: { bg: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)', border: '#90caf9' },
      xls: { bg: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)', border: '#a5d6a7' },
      xlsx: { bg: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)', border: '#a5d6a7' },
      txt: { bg: 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)', border: '#ffcc80' }
    }

    const colors = gradientColors[extension] || { bg: isDarkMode ? '#2d3a52' : '#f5f5f5', border: isDarkMode ? '#3d4a62' : '#e0e0e0' }

    return (
      <Grid item xs={6} sm={4} md={3} lg={2.4} key={`Doc${img.path_it}`}>
        <Card
          sx={{
            background: colors.bg,
            p: 0.75,
            borderRadius: 1.5,
            boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
            border: `1px solid ${colors.border}`,
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            '&:hover': {
              transform: 'translateY(-1px)',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
            }
          }}
          onClick={() => saveAs(url, img.path_it)}
        >
          <Box
            sx={{
              height: 50,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 0.5
            }}
          >
            {getFileIcon(extension)}
          </Box>
          <Typography
            sx={{
              color: isDarkMode ? '#e0e0e0' : '#424242',
              fontSize: 9,
              fontWeight: 600,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              textAlign: 'center',
              width: '100%'
            }}
          >
            {img?.nombre_it}
          </Typography>
          <Typography
            sx={{
              color: isDarkMode ? '#b0bec5' : '#757575',
              fontSize: 7,
              fontWeight: 500,
              textTransform: 'uppercase',
              mt: 0.25
            }}
          >
            {extension}
          </Typography>
        </Card>
      </Grid>
    )
  }

  const renderFile = (img) => {
    if (!img?.path_it) return null

    const extension = getFileExtension(img.path_it)
    if (!extension) return null

    // Imágenes
    const imageExtensions = ['jpg', 'png', 'jpeg', 'svg', 'x-png', 'gif', 'bmp', 'webp']
    if (imageExtensions.includes(extension)) {
      return renderImageFile(img)
    }

    // Videos
    const videoExtensions = ['wmv', 'mp4', 'avi', 'mov', 'mkv', 'flv']
    if (videoExtensions.includes(extension)) {
      return renderVideoFile(img)
    }

    // Documentos
    const documentExtensions = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'txt']
    if (documentExtensions.includes(extension)) {
      return renderDocumentFile(img, extension)
    }

    return null
  }

  if (!attachments || attachments.length === 0) {
    return null
  }

  return (
    <Box sx={{ background: isDarkMode ? '#1F2A40' : '#fff', px: 3, pb: 2 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 1.5,
          pb: 1,
          borderBottom: isDarkMode ? '2px solid #3d4a62' : '2px solid #e8eef5'
        }}
      >
        <Box
          sx={{
            width: 3,
            height: 16,
            background: 'linear-gradient(180deg, #673ab7 0%, #9575cd 100%)',
            borderRadius: '2px'
          }}
        />
        <Typography
          sx={{
            color: isDarkMode ? '#e0e0e0' : '#2c3e50',
            fontWeight: 700,
            fontSize: 14,
            letterSpacing: '0.3px'
          }}
          variant="h5"
        >
          Archivos Adjuntos ({attachments.length})
        </Typography>
      </Box>
      <Grid container spacing={1}>
        {attachments.map((img) => renderFile(img))}
      </Grid>
    </Box>
  )
}

export default AttachedFileCard
